import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { buildConsumptionMap, buildTaxMap } from '../cart';
import { sortDiscounts } from '../discount';
import type { Tax } from '../models/tax';
import { generateOrderNumber } from '../order';
import type {
    AppliedDiscount,
    Cart,
    CartItem,
    SelectedModifier,
} from '../types/cart';
import type { MappedCustomer } from '../types/customer';

export function haveSameModifierSelection(
    existingModifiers: SelectedModifier[] = [],
    incomingModifiers: SelectedModifier[] = [],
) {
    return (
        JSON.stringify(existingModifiers) === JSON.stringify(incomingModifiers)
    );
}

function mergeCartItem(cartItems: CartItem[], newItem: CartItem) {
    const existingItem = cartItems.find((item) => {
        return (
            item.itemId === newItem.itemId &&
            item.variantId === newItem.variantId &&
            item.pricingGroupId === newItem.pricingGroupId &&
            item.itemSellPrice === newItem.itemSellPrice &&
            item.notes === newItem.notes &&
            !item.modifiers &&
            !newItem.modifiers
        );
    });

    if (!existingItem) {
        return undefined;
    }

    const mergedQuantity = existingItem.quantity + newItem.quantity;

    return cartItems.map((item) => {
        if (item.id !== existingItem.id) {
            return item;
        }

        return {
            ...existingItem,
            quantity: mergedQuantity,
            subTotal: mergedQuantity * existingItem.itemSellPrice,
            total: mergedQuantity * existingItem.itemSellPrice,
        };
    });
}

interface CartStore {
    cart: Partial<Cart>;
    taxesMap: Map<string, Tax[]>;
    consumptionMap: Map<string, number>;
    updateConsumptionMap: () => Promise<void>;
    updateTaxesMap: () => Promise<void>;
    updateCart: (cart: Partial<Cart>) => void;
    addProduct: (cartItem: CartItem, mergeIfIdentical?: boolean) => CartItem;
    updateCartItem: (cartItem: CartItem) => CartItem;
    addCustomer: (
        customer: MappedCustomer,
    ) => Promise<{ discount: AppliedDiscount | undefined }>;
    addCustomerV2: (
        customer: MappedCustomer,
    ) => Promise<{ discounts: AppliedDiscount[] }>;
    resetCart: VoidFunction;
}

export const useCartStore = create(
    persist<CartStore>(
        (set, get) => ({
            cart: {},
            consumptionMap: new Map(),
            taxesMap: new Map(),
            updateConsumptionMap: async () => {
                const cartItems = get().cart.cartItems ?? [];

                if (cartItems.length === 0) {
                    set({ consumptionMap: new Map() });
                    return;
                }

                try {
                    const map = await buildConsumptionMap(cartItems);
                    set({ consumptionMap: map });
                } catch {
                    // Silently fail in test environments without DB setup
                }
            },
            updateTaxesMap: async () => {
                const cartItems = get().cart.cartItems ?? [];

                if (cartItems.length === 0) {
                    set({ taxesMap: new Map() });
                    return;
                }

                try {
                    const map = await buildTaxMap(cartItems);
                    set({ taxesMap: map });
                } catch {
                    // Silently fail in test environments without DB setup
                }
            },
            updateCart: (cart) => {
                const updatedCart = { ...get().cart, ...cart };

                if (!updatedCart.cartItems?.length) {
                    updatedCart.orderNumber = undefined;
                }

                set({ cart: updatedCart });

                const store = get();

                store.updateConsumptionMap();
                store.updateTaxesMap();
            },
            addCustomer: async (customer: MappedCustomer) => {
                const { cart } = get();

                const cartDiscounts = cart?.discounts ?? [];

                const { discountService } = await import('../services');
                const discounts =
                    await discountService.fetchDiscountByCustomerGroups(
                        customer.customerGroupIds,
                    );

                const possibleDiscounts: AppliedDiscount[] = sortDiscounts(
                    discounts.map((discount) => ({
                        ...discount,
                        rewardAmount: discount.rewardAmount ?? 0,
                        autoApplied: true,
                        scope: 'cart',
                        itemIds: [],
                        discountAmount: 0,
                    })),
                );

                set({
                    cart: {
                        ...cart,
                        customerId: customer.id,
                        discounts:
                            possibleDiscounts.length === 0
                                ? cartDiscounts
                                : [possibleDiscounts[0]],
                    },
                });

                return { discount: possibleDiscounts[0] };
            },
            addCustomerV2: async (customer: MappedCustomer) => {
                const { cart } = get();

                const cartDiscounts = cart?.discounts ?? [];

                const { discountService } = await import('../services');
                const discounts =
                    await discountService.fetchDiscountByCustomerGroups(
                        customer.customerGroupIds,
                    );

                const possibleDiscounts: AppliedDiscount[] = sortDiscounts(
                    discounts.map((discount) => ({
                        ...discount,
                        rewardAmount: discount.rewardAmount ?? 0,
                        autoApplied: true,
                        scope: 'cart',
                        itemIds: [],
                        discountAmount: 0,
                    })),
                );

                const manuallyAppliedDiscounts = cartDiscounts.filter(
                    (discount) => !discount.autoApplied,
                );

                const newDiscounts = [
                    ...new Map(
                        [...possibleDiscounts, ...manuallyAppliedDiscounts].map(
                            (discount) => [discount.id, discount],
                        ),
                    ).values(),
                ];

                set({
                    cart: {
                        ...cart,
                        customerId: customer.id,
                        discounts: newDiscounts,
                    },
                });

                return { discounts: possibleDiscounts };
            },
            addProduct: (cartItem: CartItem, mergeIfIdentical?: boolean) => {
                const cart = get().cart;
                const previousCartItems = cart.cartItems ?? [];

                const mergedItems = mergeIfIdentical
                    ? mergeCartItem(previousCartItems, cartItem)
                    : undefined;

                if (mergedItems) {
                    set({ cart: { ...cart, cartItems: mergedItems } });
                } else {
                    const orderNumber =
                        cart.orderNumber ?? generateOrderNumber();
                    const createdAt =
                        cart.createdAt ?? new Date().toISOString();
                    const id = cart.id ?? crypto.randomUUID();

                    const newCartItems = [...previousCartItems, cartItem];
                    set({
                        cart: {
                            ...cart,
                            cartItems: newCartItems,
                            orderNumber,
                            createdAt,
                            id,
                        },
                    });
                }

                const store = get();

                store.updateConsumptionMap();
                store.updateTaxesMap();

                return cartItem;
            },
            updateCartItem: (cartItem: CartItem) => {
                const cart = get().cart;
                const cartItems = cart.cartItems ?? [];
                const updatedCartItems = cartItems.map((item) =>
                    item.id === cartItem.id ? cartItem : item,
                );

                set({ cart: { ...cart, cartItems: updatedCartItems } });

                const store = get();

                store.updateConsumptionMap();
                store.updateTaxesMap();

                return cartItem;
            },
            resetCart: () =>
                set({
                    cart: {},
                    consumptionMap: new Map(),
                    taxesMap: new Map(),
                }),
        }),
        {
            name: 'cart-store',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) =>
                ({ cart: state.cart }) as unknown as CartStore,
            onRehydrateStorage: () => (state) => {
                if (state?.cart.cartItems?.length && !state.cart.id) {
                    state.cart = { ...state.cart, id: crypto.randomUUID() };
                }
                state?.updateConsumptionMap();
                state?.updateTaxesMap();
            },
        },
    ),
);
